import { FormGroup } from '@angular/forms';

import type { DropdownData, FilteredValue, RepeatableSubField, RepeatableSubFieldContext } from '../../../interfaces/input.interface';
import { RepeatableValueUtils } from './repeatable-value.utils';
import type { RepeatableLogger, RepeatableRowValue } from './repeatable-group.types';

export class RepeatableDropdownResolver {
    public constructor(
        private readonly logger: RepeatableLogger,
        private readonly getRowsSnapshotAsStrings: () => Array<Record<string, string>>,
        private readonly getRowSnapshot: (rowIndex: number) => RepeatableRowValue,
        private readonly getModel: () => FormGroup | null,
    ) {}

    public options(subField: RepeatableSubField, rowIndex: number): Array<FilteredValue<unknown>> {
        try {
            const dropdownData = this.getDropdownData(subField, rowIndex);
            const configuration = subField.input?.dropdownConfiguration;
            const filteredValues = (dropdownData?.filtered ?? []) as Array<FilteredValue<unknown>>;
            const values = (dropdownData?.values ?? []) as Array<FilteredValue<unknown>>;
            const hasFilteredValues = filteredValues.some((item) => item?.value !== null && item?.value !== undefined);
            const hasValues = values.some((item) => item?.value !== null && item?.value !== undefined);
            let baseValues: Array<FilteredValue<unknown>> = [];

            if (configuration?.filteredValues?.length) {
                baseValues = configuration.filteredValues as Array<FilteredValue<unknown>>;
            } else if (hasValues) {
                baseValues = values;
            } else if (hasFilteredValues) {
                baseValues = filteredValues;
            }

            const filter = configuration?.filter as ((it: FilteredValue<unknown>) => boolean | undefined) | undefined;
            const filteredOptions = filter
                ? baseValues.filter((item) => {
                    try {
                        return filter(item) !== false;
                    } catch (error: unknown) {
                        this.logger.warn('dropdownOptions(): filter threw, keeping item by default.', {
                            key: subField.key,
                            rowIndex,
                            error,
                        });

                        return true;
                    }
                })
                : baseValues;

            return this.toCanonicalOptions(filteredOptions, subField);
        } catch (error: unknown) {
            this.logger.warn('dropdownOptions(): unexpected error, returning empty list.', {
                key: subField.key,
                rowIndex,
                error,
            });

            return [];
        }
    }

    /**
     * Resolves a serialized id to the primitive value rendered by PrimeNG.
     *
     * Repeatable dropdown controls must store stable scalar IDs. The original object stays in `raw`
     * so domain code can still recover the full entity when needed.
     */
    public mapIdToDropdownValue(idRaw: unknown, subField: RepeatableSubField, rowIndex: number): unknown | null {
        const id = String(idRaw ?? '').trim();

        if (id === '' || RepeatableValueUtils.isNullishStringToken(id)) {
            return null;
        }

        const primaryKey = this.getControlValueKey(subField);
        const identityKeys = this.getIdentityKeys(subField);
        const options = this.options(subField, rowIndex);
        const found = options.find((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey) === id)
            ?? this.findOptionByIdentity(id, options, primaryKey, identityKeys);

        if (found) {
            return found.value;
        }

        const foundByNormalizedToken = this.mapByNormalizedOptionToken(id, options);

        if (foundByNormalizedToken !== null) {
            this.logger.log('mapIdToDropdownValue(): resolved by normalized token.', {
                key: subField.key,
                rowIndex,
                id,
            });

            return foundByNormalizedToken;
        }

        return this.mapByFallbackLabel(subField, rowIndex, id, primaryKey, options);
    }

    public idsToLabels(subField: RepeatableSubField, ids: string[], rowIndex: number): string[] {
        const primaryKey = this.getControlValueKey(subField);
        const identityKeys = this.getIdentityKeys(subField);
        const options = this.options(subField, rowIndex);

        return ids
            .map((id) => {
                const option = options.find((item) => RepeatableValueUtils.getOptionKey(item?.value, primaryKey) === id)
                    ?? this.findOptionByIdentity(id, options, primaryKey, identityKeys)
                    ?? this.findOptionByNormalizedToken(id, options);

                return option?.label ?? '';
            })
            .filter((label) => label.trim() !== '');
    }

    public mapIdToEntity<TEntity extends Record<string, unknown>>(
        idRaw: unknown,
        subField: RepeatableSubField,
        rowIndex: number,
    ): TEntity | null {
        const id = String(idRaw ?? '').trim();

        if (id === '' || RepeatableValueUtils.isNullishStringToken(id)) {
            return null;
        }

        const primaryKey = this.getControlValueKey(subField);
        const identityKeys = this.getIdentityKeys(subField);
        const options = this.options(subField, rowIndex);
        const option = options.find((item) => RepeatableValueUtils.getOptionKey(item?.value, primaryKey) === id)
            ?? this.findOptionByIdentity(id, options, primaryKey, identityKeys);
        const rawValue = option?.raw ?? option?.value ?? null;

        return rawValue !== null && typeof rawValue === 'object' && !Array.isArray(rawValue) ? rawValue as TEntity : null;
    }

    public logWhySelectCannotRender(subField: RepeatableSubField, rowIndex: number, rawId: string): void {
        const primaryKey = this.getControlValueKey(subField);
        const options = this.options(subField, rowIndex);
        const dropdownData = this.getDropdownData(subField, rowIndex);
        const baseValuesLength = Array.isArray(dropdownData?.values) ? dropdownData.values.length : 0;
        const baseFilteredLength = Array.isArray(dropdownData?.filtered) ? dropdownData.filtered.length : 0;
        const reason = options.length === 0
            ? 'Options list is empty.'
            : options.some((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey) === rawId)
                ? 'ID exists in options but UI still blank.'
                : 'ID not found in options.';

        this.logger.warn('Select cannot display value.', {
            rowIndex,
            key: subField.key,
            rawId,
            primaryKey,
            optionsLen: options.length,
            baseValuesLength,
            baseFilteredLength,
            reason,
        });
    }

    public getDropdownData(subField: RepeatableSubField, rowIndex: number): DropdownData<unknown> | undefined {
        if (typeof subField.getData === 'function') {
            try {
                return subField.getData(this.buildSubFieldContext(rowIndex));
            } catch (error: unknown) {
                this.logger.warn('getData() threw, falling back to static data.', {
                    key: subField.key,
                    rowIndex,
                    error,
                });
            }
        }

        return subField.input?.data as DropdownData<unknown> | undefined;
    }


    private getControlValueKey(subField: RepeatableSubField): string {
        return subField.input?.dropdownConfiguration?.controlValueKey
            ?? subField.input?.dropdownConfiguration?.valuePrimaryKey
            ?? 'id';
    }

    private getIdentityKeys(subField: RepeatableSubField): string[] {
        const configuration = subField.input?.dropdownConfiguration;
        const keys = new Set<string>([
            configuration?.controlValueKey,
            configuration?.valuePrimaryKey,
            configuration?.backendValueKey,
            ...(configuration?.rawIdentityKeys ?? []),
            'id',
        ].map((key) => String(key ?? '').trim()).filter((key) => key !== ''));

        return Array.from(keys);
    }

    private toCanonicalOptions(
        options: Array<FilteredValue<unknown>>,
        subField: RepeatableSubField,
    ): Array<FilteredValue<unknown>> {
        const primaryKey = this.getControlValueKey(subField);

        return options.map((option) => this.toCanonicalOption(option, primaryKey));
    }

    private toCanonicalOption(option: FilteredValue<unknown>, primaryKey: string): FilteredValue<unknown> {
        const rawValue = option.raw ?? option.value;
        const scalarValue = this.resolveScalarOptionValue(option.value, rawValue, primaryKey);

        return {
            ...option,
            value: scalarValue,
            raw: rawValue,
        };
    }

    private resolveScalarOptionValue(optionValue: unknown, rawValue: unknown, primaryKey: string): unknown {
        if (optionValue === null || optionValue === undefined) {
            return optionValue;
        }

        if (typeof optionValue !== 'object') {
            return optionValue;
        }

        const id = RepeatableValueUtils.getOptionKey(optionValue, primaryKey)
            || RepeatableValueUtils.getOptionKey(rawValue, primaryKey);

        return id !== '' ? id : optionValue;
    }


    private findOptionByIdentity(
        id: string,
        options: Array<FilteredValue<unknown>>,
        primaryKey: string,
        identityKeys: readonly string[],
    ): FilteredValue<unknown> | null {
        const lookupKeys = new Set([id, this.normalizeLooseDropdownToken(id)].filter((key) => key !== ''));
        const matches = options.filter((option) =>
            this.getComparableOptionKeys(option, primaryKey, identityKeys).some((key) => lookupKeys.has(key))
        );
        const canonicalValues = new Set(matches.map((option) => String(option.value ?? '')));

        return canonicalValues.size === 1 ? matches[0] ?? null : null;
    }

    private getComparableOptionKeys(
        option: FilteredValue<unknown>,
        primaryKey: string,
        identityKeys: readonly string[],
    ): string[] {
        const keys = new Set<string>();

        this.addComparableKeys(keys, option.value, primaryKey, identityKeys);
        this.addComparableKeys(keys, option.raw, primaryKey, identityKeys);
        this.addPrimitiveComparableKey(keys, option.label);

        return Array.from(keys);
    }

    private addComparableKeys(
        keys: Set<string>,
        value: unknown,
        primaryKey: string,
        identityKeys: readonly string[],
    ): void {
        const directKey = RepeatableValueUtils.getOptionKey(value, primaryKey);

        this.addPrimitiveComparableKey(keys, directKey);

        if (value === null || typeof value !== 'object') {
            this.addPrimitiveComparableKey(keys, value);
            return;
        }

        const record = value as Record<string, unknown>;

        for (const key of identityKeys) {
            const candidate = this.readIdentityPath(record, key);

            if (candidate !== null && candidate !== undefined && typeof candidate !== 'object') {
                this.addPrimitiveComparableKey(keys, candidate);
            }
        }
    }


    private addPrimitiveComparableKey(keys: Set<string>, value: unknown): void {
        if (value === null || value === undefined || typeof value === 'object') {
            return;
        }

        const key = String(value).trim();

        if (key === '') {
            return;
        }

        keys.add(key);
        keys.add(this.normalizeLooseDropdownToken(key));
    }

    private readIdentityPath(source: Record<string, unknown>, path: string): unknown {
        if (!path.includes('.')) {
            return source[path];
        }

        return path.split('.').reduce<unknown>((current, key) => {
            if (current !== null && typeof current === 'object') {
                return (current as Record<string, unknown>)[key];
            }

            return undefined;
        }, source);
    }

    private findOptionByNormalizedToken(id: string, options: Array<FilteredValue<unknown>>): FilteredValue<unknown> | null {
        const normalizedId = this.normalizeLooseDropdownToken(id);

        if (normalizedId === '') {
            return null;
        }

        return options.find((option) => {
            const normalizedLabel = this.normalizeLooseDropdownToken(option?.label);
            const normalizedValue = this.normalizeLooseDropdownToken(option?.value);

            return normalizedLabel === normalizedId || normalizedValue === normalizedId;
        }) ?? null;
    }

    private mapByNormalizedOptionToken(id: string, options: Array<FilteredValue<unknown>>): unknown | null {
        return this.findOptionByNormalizedToken(id, options)?.value ?? null;
    }

    private normalizeLooseDropdownToken(value: unknown): string {
        if (value === null || value === undefined || typeof value === 'object') {
            return '';
        }

        const normalizedValue = RepeatableValueUtils.normalizeOptionLabel(value).replace(/\s+/g, '');

        if (/^0+\d+$/.test(normalizedValue)) {
            return String(Number.parseInt(normalizedValue, 10));
        }

        return normalizedValue;
    }

    private mapByFallbackLabel(
        subField: RepeatableSubField,
        rowIndex: number,
        id: string,
        primaryKey: string,
        options: Array<FilteredValue<unknown>>,
    ): unknown | null {
        const fallbackLabel = this.getFallbackRowLabel(subField, rowIndex);

        if (fallbackLabel === '') {
            return null;
        }

        const normalizedFallbackLabel = RepeatableValueUtils.normalizeOptionLabel(fallbackLabel);
        const foundByLabel = options.find((option) => {
            const optionLabel = RepeatableValueUtils.normalizeOptionLabel(option?.label ?? '');

            return optionLabel !== '' && optionLabel === normalizedFallbackLabel;
        });

        if (foundByLabel) {
            this.logger.log('mapIdToDropdownValue(): resolved by fallback label.', {
                key: subField.key,
                rowIndex,
                id,
                fallbackLabel,
                resolvedId: RepeatableValueUtils.getOptionKey(foundByLabel.value, primaryKey),
            });

            return foundByLabel.value;
        }

        return null;
    }

    private buildSubFieldContext(rowIndex: number): RepeatableSubFieldContext {
        return {
            rowIndex,
            row: this.getRowSnapshot(rowIndex),
            rows: this.getRowsSnapshotAsStrings(),
            model: this.getModel() ?? new FormGroup({}),
        };
    }

    private getFallbackRowLabel(subField: RepeatableSubField, rowIndex: number): string {
        const row = this.getRowSnapshot(rowIndex);
        const labelControlKey = this.getFallbackLabelControlKey(subField);

        if (!labelControlKey) {
            return '';
        }

        return String(row[labelControlKey] ?? '').trim();
    }

    private getFallbackLabelControlKey(subField: RepeatableSubField): string | null {
        switch (subField.key) {
            case 'uso':
                return 'usoNombre';

            case 'agente':
            case 'plaga':
                return 'agenteNombre';

            default:
                return null;
        }
    }
}
